export type PrivateEventContentOptions = {
  shortName: string;
  phoneDisplay: string;
  brand: "tuscan" | "roma" | "generic";
};

export function buildPrivateEventFaqItems({
  shortName,
  phoneDisplay,
  brand,
}: PrivateEventContentOptions) {
  const isTuscan = brand === "tuscan";
  const isRoma = brand === "roma";
  const foodAndBeverageAnswer = isTuscan
    ? `${shortName} builds menus around your group from its American and Tuscan kitchen, including shared starters, salads, stone-fired pizzas, wings, burgers, and party platters. The full bar adds craft cocktails, wine, and beer. Tell the team your headcount, budget per person, and any dietary needs, and they will recommend a format.`
    : `${shortName} builds menus around your group, including shared starters, salads, mains, and platters. The full bar adds cocktails, wine, and beer. Tell the team your headcount, budget per person, and any dietary needs, and they will recommend a format.`;

  return [
    {
      question: "How far in advance should I book a private event?",
      answer:
        `Reach out as early as you can, especially for weekends, holidays, and larger groups. ${shortName} works dates on a first-come basis, so sending your request early gives the team the best chance to hold your preferred date and time. If your date is close, call ${phoneDisplay} and we will check availability right away.`,
    },
    {
      question: "Is there a minimum or maximum group size?",
      answer: isRoma
        ? `${shortName} has two formats, and both locations offer both. The private dining room is a separate, enclosed space that seats up to 10 guests — the right fit for a small celebration, a client dinner, or an intimate family gathering. A full restaurant buyout hosts up to 100 guests for larger parties, corporate functions, and holiday events. Share your estimated headcount in the request and the team will confirm which format fits, or call ${phoneDisplay}.`
        : `Group sizes vary by date, format, and how the space is set. Share your estimated headcount in the request and the team will confirm what works for your day. If you are planning a larger party or a full buyout, call ${phoneDisplay} to confirm options.`,
    },
    ...(isRoma
      ? [
          {
            question: "Does the private dining room have a door, or is it a section of the restaurant?",
            answer:
              `It is a physically separate room, not a curtained-off section of the dining room, and it seats up to 10 guests. Both the Southern Highlands and Henderson locations have one. For a group larger than 10, the format is a full restaurant buyout for up to 100. Call ${phoneDisplay} and the team will confirm which works for your date.`,
          },
          {
            question: "What does a full buyout cost?",
            answer:
              `Buyout minimums are quoted per event, because they depend on the date, day of the week, guest count, and the menu you choose. Send the date and headcount in the inquiry and the team will come back with the minimum for that specific date, or call ${phoneDisplay}.`,
          },
        ]
      : []),
    ...(isRoma
      ? [
          {
            question:
              "Can I host a private dinner or group event at the Henderson location?",
            answer:
              `Yes. ${shortName} hosts private dinners and group events at the Henderson location on West Horizon Ridge Parkway, off the 215 near Green Valley. The Henderson dining room and bar suit rehearsal dinners, corporate and team dinners, birthdays, anniversaries, and larger group gatherings, and the location serves late, open daily until 2:00 AM. Tell the team your date, headcount, and whether Henderson or Southern Highlands is closer for your guests, and they will confirm the room and format. To check a Henderson date, call ${phoneDisplay}.`,
          },
        ]
      : []),
    {
      question: "Do I need a final headcount before asking?",
      answer:
        "No. An estimate is enough to start. Share the most realistic number you have and note if it may change.",
    },
    {
      question: "How does food and beverage work for a private event?",
      answer: foodAndBeverageAnswer,
    },
    {
      question: "Can I ask about both catering and an in-restaurant event?",
      answer:
        'Yes. Choose "Not sure yet" for the format and describe what you are planning. The team can help route the inquiry, whether you want to dine in, host a full buyout, or order catering trays to go.',
    },
    {
      question: "Do you take a deposit, and how is the date confirmed?",
      answer: isRoma
        ? `A 25% deposit confirms the date. The team reviews your request first, sends the format and menu direction along with the total, and the date is held once the deposit is in. Submit the inquiry or call ${phoneDisplay} to check availability.`
        : `Deposit and confirmation details depend on the date, group size, and format, so the ${shortName} team will walk you through them after they review your request. Submit the inquiry or call ${phoneDisplay} and we will confirm availability and the next step.`,
    },
    {
      question: "What happens after I submit the inquiry?",
      answer:
        `The ${shortName} team reviews your date, guest count, event type, and notes, then follows up with the next practical step.`,
    },
  ];
}

export type PrivateEventTypeDetail = {
  title: string;
  body: string;
};

export function buildPrivateEventTypeDetails({
  shortName,
  brand,
}: Pick<PrivateEventContentOptions, "shortName" | "brand">): PrivateEventTypeDetail[] {
  const corporateBody =
    brand === "tuscan"
      ? `Team dinners, client meals, holiday parties, and business or office functions in Southern Highlands. ${shortName} handles the table setup and the menu so you can focus on the room.`
      : brand === "roma"
        ? `Team dinners, client meals, holiday parties, and business or office functions in Southern Highlands or Henderson. ${shortName} handles the table setup and the menu so you can focus on the room.`
        : `Team dinners, client meals, holiday parties, and business or office functions at ${shortName}. The team will help match the room, menu, and service to your group.`;

  return [
    {
      title: "Corporate events and office functions",
      body: corporateBody,
    },
      {
        title: "Rehearsal dinners and wedding-week meals",
        body: brand === "tuscan"
          ? "A relaxed, neighborhood-restaurant rehearsal dinner with shared plates and a full bar, set the night before the big day."
          : brand === "roma"
            ? "A relaxed Italian private dining option for rehearsal dinners and wedding-week meals in Southern Highlands or Henderson."
            : `A hosted meal at ${shortName} for rehearsal dinners, wedding-week gatherings, and post-ceremony celebrations.`,
    },
    {
      title: "Birthdays, anniversaries, and celebrations",
      body: "Birthday parties, anniversaries, engagement parties, showers, graduations, and other reasons to gather, planned around your date and guest count.",
    },
      {
        title: "Group dining and gatherings",
        body: brand === "tuscan"
          ? "Family reunions, holiday parties, retirement dinners, and memorial gatherings that need a comfortable room and an easy meal."
          : brand === "roma"
            ? "Group dining for family reunions, holiday parties, retirement dinners, anniversaries, and memorial gatherings that need a comfortable Italian restaurant room and an easy meal."
            : "Group dining for family reunions, holiday parties, retirement dinners, anniversaries, and memorial gatherings, planned around your date and headcount.",
    },
  ];
}

export type PrivateEventSpaceDetail = {
  title: string;
  body: string;
};

export function buildPrivateEventSpaceDetails({
  brand,
}: Pick<PrivateEventContentOptions, "brand">): PrivateEventSpaceDetail[] {
  if (brand === "roma") {
    return [
      {
        title: "Southern Highlands and Henderson rooms",
        body: "Private dining options start with the right Roma location for your group: Southern Highlands on South Decatur Boulevard for southwest Las Vegas guests, or Henderson on West Horizon Ridge Parkway for Green Valley and east-valley guests. The Henderson dining room and full bar handle rehearsal dinners, corporate and team dinners, birthdays, and larger group gatherings, and it serves late, open daily until 2:00 AM.",
      },
      {
        title: "Private dining room — up to 10 guests",
        body: "Both locations have a physically separate, enclosed private dining room seating up to 10. Built for small celebrations, client dinners, and intimate family gatherings that want a door rather than a corner of the dining room.",
      },
      {
        title: "Full restaurant buyout — up to 100 guests",
        body: "Exclusive use of the restaurant for larger parties, corporate functions, and holiday events. Buyout minimums are quoted per date; a 25% deposit confirms the booking.",
      },
      {
        title: "Indoor dining and bar",
        body: "Comfortable indoor seating and a full bar with cocktails, wine, and beer.",
      },
      {
        title: "Catering to go",
        body: "Party platters and custom trays for off-site events, sized to your headcount.",
      },
    ];
  }

  if (brand === "generic") {
    return [
      {
        title: "Event spaces",
        body: "Share your group size and preferred setup, and the team will confirm which dining, outdoor, private-room, or buyout options fit your date.",
      },
      {
        title: "Menus and service",
        body: "The team will help shape food, beverage, seating, and service around your occasion and budget.",
      },
      {
        title: "Planning support",
        body: "You do not need every detail before reaching out. An estimated headcount and preferred date are enough to start.",
      },
    ];
  }

  return [
    {
      title: "Fire-pit patio",
      body: "Outdoor seating on the cozy fire-pit patio for groups that want an open-air Las Vegas setting.",
    },
    {
      title: "Indoor dining and bar",
      body: "Comfortable indoor seating and a full bar with craft cocktails, wine, and beer, plus a daily happy hour from 4 to 6 PM.",
    },
    {
      title: "Catering to go",
      body: "Party platters and custom American and Tuscan trays, including pizza bundles, wings, tenders, sliders, sandwich platters, fajita platters, and salad trays.",
    },
  ];
}
